import { Controller } from 'stimulus'
import { useCheckAll } from './mixins/useCheckAll'
import { useBusItem } from './mixins/useBusItem.js'
import { useDispatch } from 'stimulus-use'
import I18n from '@app/javascript/i18n/index.js.erb'
import capitalize from 'lodash.capitalize'

export default class extends Controller {
  static targets = [
    'checkbox', 'checkAll', 'checkboxesCheckedCounter',
    'checkboxesTotal', 'bulkButtons', 'entityIds',
    'total', 'bulkExportModal', 'bulkExportButton', 'reportExportButton'
  ]

  static values = {
    type: String,
    typeScoped: String,
    persist: Boolean
  }

  connect() {
    if (!this.hasCheckAllTarget && this.checkboxTargets.length === 0) { return }

    this.typeValue ||= 'default'
    this.typeScopedValue ||= this.typeValue

    useCheckAll(this, this.persistValue, this.typeScopedValue)
    useBusItem(this)
    useDispatch(this) // Reference: https://github.com/stimulus-use/stimulus-use/blob/master/docs/use-dispatch.md
    this.checkboxesCheckedObserver = new MutationObserver(this.entitiesCheckedChanged.bind(this))
    this.checkboxesCheckedObserver.observe(this.checkboxesCheckedCounterTarget, {
      childList: true,
      attributes: true
    })
    this.toggleCheckboxPartlyChecked()
    this.updateBulkExportModalHeader()
    this.entitiesCheckedChanged()

    addEventListener('entity-select:afterCardsLoaded', (event) => {
      this.updateCheckboxesChecked()
      this.refreshCheckboxesCheckedCounter()
    })
  }

  disconnect() {
    this.checkboxesCheckedObserver.disconnect()
  }

  get entityIds() {
    return this.getReferenceIds()
  }

  toggleCheckboxPartlyChecked() {
    // Check the bulk selection checkbox, if at least 1 entity has been selected
    $(this.checkAllTarget).prop('checked', this.getCheckboxesCheckedAmount() > 0)
    // Conditionally add the "partly checked" class name to the bulk selection checkbox
    if (this.getIsAllChecked()) {
      $(this.checkAllTarget).removeClass('is-checked-partly')
    } else {
      $(this.checkAllTarget).addClass('is-checked-partly')
    }
  }

  entitiesCheckedChanged(mutation) {
    if (this.hasEntityIdsTarget) {
      this.entityIdsTarget.value = this.entityIds.join(',')
    }
    const entitiesText = `${this.entityIds.length} ${capitalize(this.typeValue)}` + (this.entityIds.length > 1 ? 's' : '')
    const checkedAmount = this.getCheckboxesCheckedAmount()
    if (checkedAmount <= 0) {
      this.bulkButtonsTargets.forEach((button) => {
        button.setAttribute('disabled', 'true')
      })
    } else {
      this.bulkButtonsTargets.forEach((button) => {
        if (this.supportsSelectedEntityIds(button)) {
          button.removeAttribute('disabled')
        } else {
          button.setAttribute('disabled', 'true')
        }

        if (typeof button.dataset.confirmTemplate !== 'undefined') {
          button.dataset.confirm = button.dataset.confirmTemplate.replace('[x]', entitiesText)
        }
        if (typeof button.dataset.titleTemplate !== 'undefined') {
          button.dataset.title = button.dataset.titleTemplate.replace('[x]', entitiesText)
        }
      })
    }

    this.toggleCheckboxPartlyChecked()
    this.updateBulkExportModalHeader()

    this.dispatch('changed', { selectedEntityIds: this.entityIds })
  }

  supportsSelectedEntityIds(element) {
    const supportedEntityTypes = element.dataset.supportedEntityTypes

    if (!supportedEntityTypes) return true
    if (supportedEntityTypes === '[]') return true

    return this.entityIds.every((id) => {
      const type = id.split('-')[0]
      return supportedEntityTypes.includes(type)
    })
  }

  processBulkDeleteSuccess(data) {
    this.checkboxesCheckedCounterTarget.innerHTML = '0'
    this.setIsAllChecked(false)
    this.setAllCheckboxesWithChecked(false)
    this.entitiesCheckedChanged()
  }

  handleExportActions() {
    this.setExportEntityIds()
    this.setExportSearchAttributes()
    this.setExportTotalValue()
  }

  setExportEntityIds() {
    const exportController = this.getBusItem('export-generator')
    exportController.entityIdsValue = this.getReferenceIds()
  }

  setExportSearchAttributes() {
    const exportController = this.getBusItem('export-generator')
    const filtersContainerController = this.actionFiltersContainerController()
    const searchParams = $(filtersContainerController.getFormTarget()).serialize()
    exportController.searchAttributesValue = searchParams
  }

  setExportTotalValue() {
    const exportController = this.getBusItem('export-generator')
    exportController.totalValue = this.total()
  }

  total() {
    let n = Number(this.totalTarget.innerHTML)
    if (isNaN(n)) {
      n = 10000
      console.log(`Unable to determine number of entities, using ${n}`)
    }

    return n
  }

  actionFiltersContainerController() {
    const identifier = 'filters-container'
    const element = document.querySelector('#' + identifier)
    return this.application.getControllerForElementAndIdentifier(element, identifier)
  }

  updateBulkExportModalHeader() {
    if (!this.hasBulkExportModalTarget) {
      return
    }

    const modalTitle = this.bulkExportModalTarget.querySelector('.c-modal__container-header-title')

    if (modalTitle) {
      const count = this.getCheckboxesChecked()?.length || 0

      if (count) {
        modalTitle.innerText = I18n.t(`export_entities.title.${this.typeValue}`, { count })
      } else {
        modalTitle.innerText = I18n.t(`export_entities.title.${this.typeValue}.all`)
      }
    }
  }

  updateReportButtonLabel(count) {
    if (this.hasReportExportButtonTarget) {
      let targetEl = this.reportExportButtonTarget
      const buttonHasIcon = targetEl.children.length > 1
      if (buttonHasIcon) {
        targetEl = targetEl.children[1]
      }
      count = parseInt(count)
      if (count > 500) {
        targetEl.innerText = I18n.t('actions.report.create.title.first')
      } else if (count === 1) {
        targetEl.innerText = I18n.t('actions.report.create.title.one')
      } else {
        targetEl.innerText = I18n.t('actions.report.create.title.other', { count })
      }
    }
  }

  updateExportButtonLabel(count) {
    if (this.hasBulkExportButtonTarget) {
      let targetEl = this.bulkExportButtonTarget

      const buttonHasIcon = targetEl.children.length > 1
      if (buttonHasIcon) {
        targetEl = targetEl.children[1]
      }

      count = parseInt(count)
      targetEl.innerText = I18n.t(`export_entities.title.${this.typeValue}`, { count })
    }
  }
}
